import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useControlled } from '@dni-react-hooks/use-controlled'
import { useMergeRefs } from '@dni-react-hooks/use-merge-refs'
import { useOnClickAway } from '@dni-react-hooks/use-on-click-away'
import { useTimeout } from '@dni-react-hooks/use-timeout'
import * as stylex from '@stylexjs/stylex'
import { useEffect, useRef, useState, type ComponentPropsWithoutRef, type ForwardedRef, type ReactNode } from 'react'
import { LinearProgress } from '../LinearProgress/LinearProgress'
import { Paper } from '../Paper/Paper'
import { Text } from '../Text/Text'
import { type MobileMediaQuery } from '../tokens/breakpoints.stylex'
import { colors } from '../tokens/colors.stylex'
import { zIndex } from '../tokens/zIndex.stylex'
import { useXStyles, type XStyles } from '../useXStyles/useXStyles'

const grow = stylex.keyframes({
  '0%': { opacity: 0, transform: 'scale(0.8)' },
  '100%': { opacity: 1, transform: 'scale(1)' },
})

const shrink = stylex.keyframes({
  '0%': { opacity: 1, transform: 'scale(1)' },
  '100%': { opacity: 0, transform: 'scale(0.8)' },
})

const verticalStyles = stylex.create({
  top: (top: number | string) => ({
    top,
  }),
  bottom: (bottom: number | string) => ({
    bottom,
  }),
})

const MOBILE: MobileMediaQuery = '@media (max-width: 480px)'
const MOBILE_SCREEN_PADDING = 8

const horizontalStyles = stylex.create({
  left: (left: number | string) => ({
    left: {
      [MOBILE]: MOBILE_SCREEN_PADDING,
      default: left,
    },
    right: {
      [MOBILE]: MOBILE_SCREEN_PADDING,
      default: 'auto',
    },
  }),
  center: {
    transform: {
      [MOBILE]: 'none',
      default: 'translateX(-50%)',
    },
    left: {
      [MOBILE]: MOBILE_SCREEN_PADDING,
      default: '50%',
    },
    right: {
      [MOBILE]: MOBILE_SCREEN_PADDING,
      default: 'auto',
    },
  },
  right: (right: number | string) => ({
    left: {
      [MOBILE]: MOBILE_SCREEN_PADDING,
      default: 'auto',
    },
    right: {
      [MOBILE]: MOBILE_SCREEN_PADDING,
      default: right,
    },
  }),
})

const _baseStyles = stylex.create({
  root: {
    position: 'fixed',
    zIndex: zIndex.snackbar,
  },
  open: {
    animationDuration: '225ms',
    animationFillMode: 'forwards',
    animationName: grow,
    animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
  close: {
    animationDuration: '225ms',
    animationFillMode: 'forwards',
    animationName: shrink,
    animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
  snackbar: {
    padding: 12,
    borderRadius: '4px',
    gap: 8,
    overflow: 'hidden',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  icon: {
    alignItems: 'center',
    display: 'flex',
    fontSize: 20,
    justifyContent: 'center',
  },
  message: {
    flexGrow: 1,
    width: 'max-content',
  },
  action: {
    display: 'flex',
    flexShrink: 0,
    marginLeft: 'auto',
  },
  progressBar: {
    borderRadius: 0,
    color: colors.red600,
    position: 'absolute',
    bottom: 0,
    height: 2,
    left: 0,
    right: 0,
  },
})

const modeStyles = stylex.create({
  dark: {
    backgroundColor: colors.grey800,
    color: colors.white,
  },
})

const defaultOffsets = { top: 24, bottom: 24, left: 24, right: 24 }

export interface SnackbarOrigin {
  /** 垂直方向 */
  vertical: 'top' | 'bottom'
  /** 水平方向 */
  horizontal: 'left' | 'center' | 'right'
}

export type SnackbarMode = 'light' | 'dark'

export interface SnackbarProps extends ComponentPropsWithoutRef<'div'> {
  /**
   * 根元素的 ref
   */
  rootRef?: ForwardedRef<HTMLDivElement>
  /**
   * 根元素的 data-testid（E2E 定位用）
   */
  'data-testid'?: string
  /**
   * 根元素的自訂樣式
   */
  rootStyle?: stylex.StyleXStyles
  /**
   * 自訂內部子元素樣式覆寫
   */
  xStyles?: Partial<XStyles<keyof typeof _baseStyles>>
  /**
   * 是否顯示 Snackbar
   */
  open?: boolean
  /**
   * 錨點位置
   * @default { vertical: 'top', horizontal: 'center' }
   */
  anchorOrigin?: SnackbarOrigin
  /**
   * 顯示模式
   * - `light`：白底深色文字（預設）
   * - `dark`：深底白色文字
   * @default 'light'
   */
  mode?: SnackbarMode
  /**
   * 各方向的偏移量
   * @default { top: 24, bottom: 24, left: 24, right: 24 }
   */
  offsets?: {
    top?: number | string
    bottom?: number | string
    left?: number | string
    right?: number | string
  }
  /**
   * 自訂圖示
   */
  icon?: ReactNode
  /**
   * 訊息內容
   */
  message?: ReactNode
  /**
   * 右側操作區域內容
   */
  action?: ReactNode
  /**
   * 自動關閉的等待毫秒數，null 表示不自動關閉
   * @default null
   */
  autoHideDuration?: number | null
  /**
   * 是否顯示倒數進度條（需搭配 autoHideDuration）
   * @default false
   */
  showProgressBar?: boolean
  /**
   * 是否停用點擊外部關閉行為
   * @default false
   */
  disableCloseOnClickAway?: boolean
  /**
   * 自訂子元素，傳入時將取代預設的訊息內容
   */
  children?: ReactNode
  /**
   * 關閉時的回呼
   */
  onClose?: () => void
  /**
   * 進場動畫開始時的回呼
   */
  // TODO: Need create shared enter and exit animation component
  onEnter?: () => void
  /**
   * 退場動畫結束時的回呼
   */
  onExited?: () => void
}

/**
 * Snackbar 輕量通知元件，用於在畫面邊緣顯示短暫的訊息提示。
 * 支援自動關閉、錨點定位、深淺色模式與倒數進度條。
 */
export const Snackbar = (props: SnackbarProps) => {
  const {
    rootRef: rootRefProp,
    rootStyle,
    xStyles,
    'data-testid': dataTestId,
    open: openProps,
    anchorOrigin = { vertical: 'top', horizontal: 'center' },
    offsets: offsetsProps,
    icon,
    message,
    action,
    autoHideDuration = null,
    showProgressBar = false,
    mode = 'light',
    onClose,
    onEnter,
    onExited,
    children,
    disableCloseOnClickAway = false,
  } = props
  const [exited, setExited] = useState(true)
  const [open, setOpen] = useControlled({
    controlled: openProps,
    default: false,
  })
  const baseStyles = useXStyles(_baseStyles, xStyles)
  const rootRef = useRef<HTMLDivElement>(null)
  const mergeRef = useMergeRefs(rootRefProp, rootRef)
  const shouldShowProgress = showProgressBar && autoHideDuration
  const offsets = { ...defaultOffsets, ...offsetsProps }

  const handleClose = () => {
    setOpen(false)
    onClose?.()
  }

  useTimeout(handleClose, open ? autoHideDuration : null)
  useOnClickAway({
    ref: rootRef,
    callback: disableCloseOnClickAway ? undefined : handleClose,
  })

  const handleEnter = useCallbackEvent(() => {
    setExited(false)
    onEnter?.()
  })

  const handleExited = () => {
    if (!open) {
      setExited(true)
      onExited?.()
    }
  }

  useEffect(() => {
    if (open) {
      handleEnter()
    }
  }, [handleEnter, open])

  if (!open && exited) {
    return null
  }

  return (
    <div
      ref={mergeRef}
      data-testid={dataTestId}
      {...stylex.props(
        baseStyles.root,
        verticalStyles[anchorOrigin.vertical](offsets[anchorOrigin.vertical]),
        anchorOrigin.horizontal === 'center' ? horizontalStyles.center : horizontalStyles[anchorOrigin.horizontal](offsets[anchorOrigin.horizontal]),
        rootStyle,
      )}
    >
      <div {...stylex.props(open ? baseStyles.open : baseStyles.close)} onAnimationEnd={handleExited}>
        {children || (
          <Paper rootStyle={[baseStyles.snackbar, mode === 'dark' && modeStyles.dark]}>
            {icon && <div {...stylex.props(baseStyles.icon)}>{icon}</div>}
            {message && <div {...stylex.props(baseStyles.message)}><Text variant="body">{message}</Text></div>}
            {action && <div {...stylex.props(baseStyles.action)}>{action}</div>}
            {shouldShowProgress && <LinearProgress rootStyle={baseStyles.progressBar} variant="countdown" value={autoHideDuration} />}
          </Paper>
        )}
      </div>
    </div>
  )
}
